import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.lg};
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 800px;
  width: 100%;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.colors.primary};
  color: white;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform ${props => props.theme.transitions.fast};

  &:hover {
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.xl};
  overflow-y: auto;
  flex: 1;

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.3rem;
    margin-top: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    padding-bottom: ${props => props.theme.spacing.sm};
    border-bottom: 2px solid ${props => props.theme.colors.primaryLight};

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
    margin-top: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  p {
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: ${props => props.theme.spacing.md};
  }

  ul, ol {
    margin-left: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};

    li {
      line-height: 1.8;
      color: ${props => props.theme.colors.textLight};
      margin-bottom: ${props => props.theme.spacing.sm};
    }
  }

  strong {
    color: ${props => props.theme.colors.text};
    font-weight: 600;
  }

  hr {
    border: none;
    border-top: 1px solid ${props => props.theme.colors.border};
    margin: ${props => props.theme.spacing.xl} 0;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;

    &:hover {
      color: ${props => props.theme.colors.primaryDark};
    }
  }
`;

const LastUpdated = styled.div`
  text-align: right;
  color: ${props => props.theme.colors.textLight};
  font-size: 0.9rem;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>개인정보처리방침</ModalTitle>
              <CloseButton onClick={onClose}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <p>
                <strong>나우바이오 ("회사")</strong>는 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고
                개인정보와 관련한 이용자의 고충을 원활하게 처리할 수 있도록 다음과 같은 처리방침을 두고 있습니다.
              </p>
              <p>
                고객님의 정보는 개인의 소중한 자산인 동시에 회사 운영의 중요한 자료가 됩니다.
                그러므로 회사는 운영상의 모든 과정에서 고객님의 개인정보를 보호하는데 최선의 노력을 다할 것을 약속 드립니다.
              </p>
              <p>
                회사는 개인정보처리방침을 개정하는 경우 웹사이트를 통하여 공지할 것입니다.
              </p>

              <h2>제 1장. 개인정보 수집 동의절차</h2>
              <p>
                회사는 귀하께서 웹사이트의 개인정보보호방침 또는 이용약관의 내용에 대해 <strong>"동의한다"</strong> 버튼 또는
                <strong>"동의하지 않는다"</strong> 버튼을 클릭할 수 있는 절차를 마련하여 <strong>"동의한다"</strong> 버튼을
                클릭하면 개인정보 수집에 대해 동의한 것으로 봅니다.
              </p>

              <h2>제 2장. 개인정보의 수집 및 이용 목적</h2>
              <p>
                회사는 개인정보를 다음의 목적을 위해 활용합니다. 처리한 개인정보는 다음의 목적 이외의 용도로는 사용되지 않으며
                이용 목적이 변경될 시 사전동의를 구할 예정입니다.
              </p>

              <h3>가. 성명, 아이디, 비밀번호</h3>
              <p>회원 가입의사 확인, 서비스 부정이용 방지, 접속 빈도파악, 회원제 서비스 이용에 따른 본인 식별 절차</p>

              <h3>나. 이메일주소, 전화번호 (수신여부 확인)</h3>
              <p>
                고지사항 전달, 본인 의사 확인, 불만 처리 등 원활한 의사소통 경로의 확보, 이벤트 및 광고성 정보 제공 및
                참여기회 제공, 접속 빈도파악 또는 회원의 서비스이용에 대한 통계
              </p>

              <h3>다. 주소, 전화번호</h3>
              <p>경품과 물품 배송에 대한 정확한 배송지의 확보</p>

              <h2>제 3장. 광고정보의 전송</h2>
              <h3>가.</h3>
              <p>회사는 귀하의 명시적인 수신거부의사에 반하여 영리목적의 광고성 정보를 전송하지 않습니다.</p>

              <h3>나.</h3>
              <p>
                회사는 약관변경, 기타 서비스 이용에 관한 변경사항, 새로운 서비스/신상품이나 이벤트 정보, 기타 상품정보 등을
                전자우편, 휴대폰 문자전송 기타 전자적 전송매체 등의 방법으로 알려드립니다. 이 경우 관련 법령상 명시사항 및
                명시방법을 준수합니다.
              </p>

              <h3>다.</h3>
              <p>
                회사는 상품정보 안내 등 온라인 마케팅을 위해 광고성 정보를 전자우편 등으로 전송하는 경우 전자우편의 제목란 및
                본문란에 귀하가 쉽게 알아 볼 수 있도록 조치합니다.
              </p>

              <h2>제 4장. 개인정보의 수집범위</h2>
              <p>
                회사는 별도의 회원가입 절차 없이 대부분의 콘텐츠에 자유롭게 접근할 수 있습니다. 회원제 서비스를 이용하고자 할 경우
                다음의 정보를 입력해주셔야 합니다.
              </p>

              <h3>가. 개인정보 수집항목</h3>
              <p>
                이름, 이메일, 생년월일, 아이디, 비밀번호, 성별, 주소, 휴대폰 번호(전화), 이메일 수신여부, SMS 수신여부,
                서비스 이용기록, 접속로그, 받는 고객 정보(이름, 전화번호, 주소, 이메일)
              </p>

              <h3>나. 개인정보 수집방법</h3>
              <p>홈페이지 내 회원가입 및 게시판 등</p>

              <h2>제 5장. 개인정보의 보유 및 이용기간</h2>
              <p>회사는 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
              <p>단, 다음의 정보에 대해서는 아래의 이유로 명시한 기간 동안 보존합니다.</p>
              <ul>
                <li><strong>보존항목</strong>: 이름, 로그인 ID, 비밀번호, 이메일, 생년월일</li>
                <li><strong>보존근거</strong>: 불량 회원의 부정한 이용의 재발 방지</li>
                <li><strong>보존기간</strong>: 1개월</li>
              </ul>
              <p>
                그리고 관계법령의 규정에 의하여 보존할 필요가 있는 경우 회사는 아래와 같이 관계법령에서 정한 일정한 기간 동안
                회원정보를 보관합니다.
              </p>
              <ul>
                <li><strong>소비자의 불만 또는 분쟁처리에 관한 기록</strong>: 3년 (전자상거래 등에서의 소비자보호에 관한 법률)</li>
              </ul>

              <h2>제 6장. 개인정보의 파기 절차 및 방법</h2>
              <p>회사는 원칙적으로 개인정보 처리목적이 달성된 경우에는 지체 없이 해당 개인정보를 파기합니다. 파기의 절차, 기한 및 방법은 다음과 같습니다.</p>

              <h3>파기절차</h3>
              <p>
                회원님이 회원가입 등을 위해 입력하신 정보는 목적 달성 후 별도의 DB로 옮겨져(종이의 경우 별도의 서류함) 내부 방침 및
                기타 관련 법령에 의한 정보보호 사유에 따라(보유 및 이용기간 참조) 일정 기간 저장 된 후 파기합니다. 별도 DB로 옮겨진
                개인정보는 법률에 의한 경우가 아니고서는 보유 이외의 다른 목적으로 이용되지 않습니다.
              </p>

              <h3>파기기한</h3>
              <p>
                이용자의 개인정보는 개인정보의 보유기간이 경과된 경우에는 보유기간의 종료일로부터 5일 이내에, 개인정보의 처리 목적 달성,
                해당 서비스의 폐지, 사업의 종료 등 그 개인정보가 불필요하게 되었을 때에는 개인정보의 처리가 불필요한 것으로 인정되는
                날로부터 5일 이내에 그 개인정보를 파기합니다.
              </p>

              <h3>파기방법</h3>
              <p>전자적 파일 형태의 정보는 기록을 재생할 수 없는 기술적 방법을 사용합니다.</p>

              <h2>제 7장. 개인정보의 안전성 확보 조치</h2>
              <p>회사는 개인정보보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 조치를 하고 있습니다.</p>

              <h3>가. 개인정보 취급 직원의 최소화 및 교육</h3>
              <p>개인정보를 취급하는 직원을 지정하고 담당자에 한정시켜 최소화 하여 개인정보를 관리하는 대책을 시행하고 있습니다.</p>

              <h3>나. 개인정보의 암호화</h3>
              <p>
                이용자의 개인정보는 비밀번호는 암호화 되어 저장 및 관리되고 있어, 본인만이 알 수 있으며 중요한 데이터는 파일 및
                전송 데이터를 암호화 하거나 파일 잠금 기능을 사용하는 등의 별도 보안기능을 사용하고 있습니다.
              </p>

              <h2>제 8장. 개인정보 보호책임자</h2>
              <p>
                회사는 고객님께서 정보를 안전하게 이용할 수 있도록 최선을 다하고 있습니다. 고객님의 개인정보를 취급하는 책임자는
                다음과 같으며 개인정보 관련 문의사항에 신속하고 성실하게 답변해드리고 있습니다.
              </p>

              <h3>▶ 개인정보보호 담당자</h3>
              <ul>
                <li><strong>이름</strong>: {t('about.company.ceoName')}</li>
                <li><strong>소속/직위</strong>: {t('about.company.ceo')}</li>
                <li><strong>E-mail</strong>: {t('about.company.emailValue')}</li>
                <li><strong>전화번호</strong>: {t('about.location.headquarters.tel')}</li>
              </ul>
              <p>
                정보주체께서는 회사 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을
                개인정보 보호책임자 및 담당자에게 문의하실 수 있습니다.
              </p>

              <h2>제 9장. 의견수렴 및 불만처리</h2>
              <p>
                회사는 회원님의 의견을 소중하게 생각하며, 회원님은 의문사항으로부터 언제나 성실한 답변을 받을 권리가 있습니다.
                회사는 회원님과의 원활한 의사소통을 위해 고객센터를 운영하고 있습니다.
              </p>
              <p>
                실시간 상담 및 전화상담은 영업시간에만 가능합니다. 이메일 및 우편을 이용한 상담은 수신 후 24시간 내에 성실하게
                답변 드리겠습니다. 다만, 근무시간 이후 또는 주말 및 공휴일에는 익일 처리하는 것을 원칙으로 합니다.
              </p>

              <h3>도용된 개인정보에 대한 회사의 조치</h3>
              <ol>
                <li>
                  이용자가 타인의 기타 개인정보를 도용하여 회원가입 등을 하였음을 알게 된 때에는 지체 없이 해당 아이디에 대한
                  서비스 이용정지 또는 회원탈퇴 등 필요한 조치를 취합니다.
                </li>
                <li>
                  자신의 개인정보 도용을 인지한 이용자가 해당 아이디에 대해 서비스 이용정지 또는 회원탈퇴를 요구하는 경우에는
                  즉시 조치를 취합니다.
                </li>
              </ol>

              <h3>기타 상담 기관</h3>
              <p>기타 개인정보에 관한 상담이 필요한 경우에는 다음 기관으로 문의하실 수 있습니다.</p>
              <ul>
                <li><strong>개인정보침해센터</strong>: <a href="http://www.118.or.kr" target="_blank" rel="noopener noreferrer">http://www.118.or.kr</a></li>
                <li><strong>대검찰청 인터넷범죄수사센터</strong>: <a href="http://icic.sppo.go.kr" target="_blank" rel="noopener noreferrer">http://icic.sppo.go.kr</a></li>
                <li><strong>경찰청 사이버테러대응센터</strong>: <a href="http://www.police.go.kr/ctrc/ctrc_main.htm" target="_blank" rel="noopener noreferrer">http://www.police.go.kr/ctrc/ctrc_main.htm</a></li>
              </ul>

              <h2>제 10장. 고지의 의무</h2>
              <p>
                이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는
                변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.
              </p>

              <LastUpdated>
                <strong>시행일</strong>: 2003년 2월<br />
                <strong>최종 업데이트</strong>: 2025년 10월 15일
              </LastUpdated>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
